import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useGlossary } from '../context/GlossaryContext.jsx'
import { useSite } from '../context/SiteContext.jsx'
import { useToast } from '../context/ToastContext.jsx'

const blank = { term: '', aka: '', def: '' }

export default function AdminGlossary() {
  const { glossary, source, createTerm, updateTerm, deleteTerm, seedStarter } = useGlossary()
  const { configured } = useSite()
  const toast = useToast()
  const [editing, setEditing] = useState(null) // term | 'new' | null
  const [f, setF] = useState(blank)
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [q, setQ] = useState('')

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const startNew = () => { setF(blank); setEditing('new') }
  const startEdit = (t) => { setF({ term: t.term, aka: t.aka || '', def: t.def }); setEditing(t) }

  const save = async () => {
    if (!f.term.trim()) return toast('Term is required', 'red')
    if (!f.def.trim()) return toast('Definition is required', 'red')
    setSaving(true)
    const res = editing === 'new' ? await createTerm(f) : await updateTerm(editing.id, f)
    setSaving(false)
    if (res?.error) return toast(res.error, 'red')
    toast('Term saved', 'green')
    setEditing(null)
  }

  const remove = async (t) => {
    if (!window.confirm(`Delete “${t.term}”?`)) return
    const res = await deleteTerm(t.id)
    if (res?.error) return toast(res.error, 'red')
    toast('Term deleted', 'default')
  }

  const doSeed = async () => {
    setSeeding(true)
    const res = await seedStarter()
    setSeeding(false)
    if (res?.error) return toast(res.error, 'red')
    toast(res.count ? `Imported ${res.count} terms` : 'Already up to date', 'green')
  }

  const filtered = glossary.filter((t) => !q || t.term.toLowerCase().includes(q.toLowerCase()))
  const editable = source === 'db' // rows have real ids only when loaded from the DB

  return (
    <main className="page">
      <div className="container">
        <div className="between wrap" style={{ marginBottom: 18 }}>
          <div>
            <span className="pill blue" style={{ marginBottom: 8 }}>Admin</span>
            <h1 className="section-title">Glossary</h1>
            <p className="section-sub mb-0">{glossary.length} terms · <Link to="/glossary">view public glossary →</Link></p>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {editable && <button className="btn btn-primary btn-sm" onClick={startNew}>+ New term</button>}
          </div>
        </div>

        {!configured && (
          <div className="config-notice" style={{ marginBottom: 18 }}>
            <strong>Backend not connected.</strong>
            <p>Editing needs Supabase. Add your keys and run <code>supabase/glossary.sql</code>, then reload.</p>
          </div>
        )}
        {configured && source !== 'db' && (
          <div className="config-notice" style={{ marginBottom: 18 }}>
            <strong>Glossary table is empty (showing bundled terms).</strong>
            <p style={{ marginBottom: 10 }}>Run <code>supabase/glossary.sql</code> in Supabase, then import the {glossary.length} bundled terms to start editing.</p>
            <button className="btn btn-primary btn-sm" onClick={doSeed} disabled={seeding}>
              {seeding ? 'Importing…' : 'Import starter glossary'}
            </button>
          </div>
        )}

        {editing && (
          <div className="card" style={{ marginBottom: 18, padding: 18 }}>
            <h3 style={{ marginTop: 0 }}>{editing === 'new' ? 'New term' : `Edit “${editing.term}”`}</h3>
            <label className="field">
              <span className="field-label">Term</span>
              <input className="input" value={f.term} onChange={set('term')} placeholder="Bacteriostatic water" />
            </label>
            <label className="field">
              <span className="field-label">Also known as <span className="muted">(optional)</span></span>
              <input className="input" value={f.aka} onChange={set('aka')} placeholder="bac water" />
            </label>
            <label className="field">
              <span className="field-label">Definition</span>
              <textarea className="input" rows={4} value={f.def} onChange={set('def')} placeholder="Plain-language definition…" />
            </label>
            <div className="row" style={{ gap: 8, marginTop: 6 }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}

        <div className="lib-search" style={{ margin: '4px 0 12px' }}>
          <span className="lib-search-icon">🔍</span>
          <input className="input" placeholder="Filter terms…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        <div className="admin-list">
          {filtered.map((t) => (
            <div className="card admin-row" key={t.slug} style={{ padding: 14 }}>
              <div style={{ minWidth: 0 }}>
                <strong>{t.term}</strong>{t.aka && <span className="muted small"> · {t.aka}</span>}
                <p className="muted small" style={{ margin: '4px 0 0' }}>{t.def}</p>
              </div>
              {editable && (
                <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit(t)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(t)}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </main>
  )
}
